import React, { useMemo } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2, ClipboardX, LayoutDashboard, Layers, Server, ShieldAlert, ShieldCheck } from 'lucide-react';
import { ControlUniverseTable } from './ControlUniverseTable.tsx';
import { TestingStatusTable } from './TestingStatusTable.tsx';
import { IssuesPipelineKanban } from './IssuesPipelineKanban.tsx';
import { CoverageMatrixTable } from './CoverageMatrixTable.tsx';
import { MRCPanel } from './MRCPanel.tsx';
import { ExceptionsSummaryChart } from './ExceptionsSummaryChart.tsx';
import { AuditorRelianceTable } from './AuditorRelianceTable.tsx';
import { ActionPlanAgingTable } from './ActionPlanAgingTable.tsx';
import { ControlsByDeptChart } from './ControlsByDeptChart.tsx';
import { ITGCCoverageBars } from './ITGCCoverageBars.tsx';
import { TesterWorkloadPanel } from './TesterWorkloadPanel.tsx';
import { ScopeControlsByRiskChart } from '../scope/ScopeControlsByRiskChart.tsx';
import { ScopeRisksMappedDonut } from '../scope/ScopeRisksMappedDonut.tsx';
import { ScopePlannedCompletedChart } from '../scope/ScopePlannedCompletedChart.tsx';
import { ScopeOverdueDetail } from '../scope/ScopeOverdueDetail.tsx';
import { PageHeader, SectionHeader } from '../controller/shared.tsx';
import {
  useCaeControlUniverse,
  useCaeCoverageMatrix,
  useCaeIssuesPipeline,
  useCaeTestingStatus,
} from '../../../hooks/analytics/useCaeReports.ts';
import {
  useScopeOverdueSummary,
  useScopeRiskSummary,
  useScopeSystems,
} from '../../../hooks/analytics/useScopeReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['department', 'status', 'frequency', 'classification'];

/**
 * CAE / Manager persona analytics dashboard (Feature 5.3, reports #9-#19).
 *
 * IA: PageHeader -> slicer bar -> always-visible KPI row -> tabbed body. The
 * slicer bar and KPI row are driven off the Control Universe Inventory rows
 * (report #9) — the persona's org-wide control catalogue. The resulting
 * filtered control-id set (and, for the department-only slicer, the plain
 * department string) is threaded into the other widgets as `controlIdFilter`
 * / `deptFilter` so most of the page responds to the slicer bar — see the
 * per-widget notes below for the few that don't share a joinable column.
 * Each widget still owns its own React Query fetch.
 */
export const CAEDashboard: React.FC = () => {
  const { data: universeData } = useCaeControlUniverse();
  const universeRows = useMemo(() => universeData ?? [], [universeData]);
  const { data: testingStatusData } = useCaeTestingStatus();
  const { data: issuesData } = useCaeIssuesPipeline();
  const { data: coverageMatrixData } = useCaeCoverageMatrix();

  // Scope & Coverage (feedback round 2026-08) — risk totals, systems, overdue.
  const { data: riskSummaryData } = useScopeRiskSummary();
  const { data: systemsData } = useScopeSystems();
  const { data: overdueData } = useScopeOverdueSummary();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      { key: 'department', label: 'Department', options: deriveSlicerOptions(universeRows, (r) => r.department) },
      { key: 'status', label: 'Status', options: deriveSlicerOptions(universeRows, (r) => r.status) },
      { key: 'frequency', label: 'Frequency', options: deriveSlicerOptions(universeRows, (r) => r.frequency) },
      {
        key: 'classification',
        label: 'Classification',
        options: deriveSlicerOptions(universeRows, (r) => r.classification),
      },
    ],
    [universeRows]
  );

  const filteredUniverseRows = useMemo(
    () =>
      applySlicers(universeRows, slicers.values, {
        department: (r) => r.department,
        status: (r) => r.status,
        frequency: (r) => r.frequency,
        classification: (r) => r.classification,
      }),
    [universeRows, slicers.values]
  );

  const controlIdFilter = useMemo(
    () => new Set(filteredUniverseRows.map((r) => r.control_id)),
    [filteredUniverseRows]
  );
  const deptFilter = slicers.values.department || null;

  const kpis = useMemo<KpiItem[]>(() => {
    const universe = filteredUniverseRows.length;
    const mrcCount = filteredUniverseRows.filter((r) => r.mrc_control).length;

    const openIssues = (issuesData ?? []).filter((r) => !deptFilter || (r.department ?? '') === deptFilter).length;

    const risks = coverageMatrixData ?? [];
    const riskCoveragePct =
      risks.length > 0 ? Math.round((100 * risks.filter((r) => r.mapped_controls > 0).length) / risks.length) : null;

    const statusRows = (testingStatusData ?? []).filter((r) => controlIdFilter.has(r.control_id));
    const completePct =
      statusRows.length > 0
        ? Math.round((100 * statusRows.filter((r) => r.workflow_status === 'completed').length) / statusRows.length)
        : null;

    return [
      {
        key: 'universe',
        title: 'Control Universe',
        value: universe,
        icon: <Layers className="h-5 w-5" />,
        color: 'bg-purple-100 text-purple-600',
      },
      {
        key: 'risk_coverage',
        title: 'Risk Coverage',
        value: riskCoveragePct != null ? `${riskCoveragePct}%` : '—',
        icon: <ShieldCheck className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
        subtitle: 'risks with a mapped control',
      },
      {
        key: 'open_issues',
        title: 'Open Issues',
        value: openIssues,
        icon: <AlertOctagon className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
      },
      {
        key: 'mrc',
        title: 'MRC Controls',
        value: mrcCount,
        icon: <LayoutDashboard className="h-5 w-5" />,
        color: 'bg-indigo-100 text-indigo-600',
      },
      {
        key: 'testing_complete',
        title: 'Testing Complete',
        value: completePct != null ? `${completePct}%` : '—',
        icon: <CheckCircle2 className="h-5 w-5" />,
        color: 'bg-emerald-100 text-emerald-600',
      },
    ];
  }, [filteredUniverseRows, issuesData, coverageMatrixData, testingStatusData, controlIdFilter, deptFilter]);

  /**
   * Scope & Coverage KPI tiles (feedback round 2026-08) — risk totals by
   * level, mapped/unmapped, systems in scope, and overdue testing/PBC —
   * recomposing CAE to the same "scope band" pattern the CFO and Controller
   * dashboards now share, plus the persona-specific overdue drill-through.
   */
  const scopeKpis = useMemo<KpiItem[]>(() => {
    const byLevel = new Map((riskSummaryData?.by_level ?? []).map((r) => [r.level.toLowerCase(), r.count]));
    return [
      { key: 'risk_high', title: 'High Risks', value: byLevel.get('high') ?? 0, icon: <AlertTriangle className="h-5 w-5" />, color: 'bg-rose-100 text-rose-600' },
      { key: 'risk_medium', title: 'Medium Risks', value: byLevel.get('medium') ?? 0, icon: <ShieldAlert className="h-5 w-5" />, color: 'bg-amber-100 text-amber-600' },
      { key: 'risk_low', title: 'Low Risks', value: byLevel.get('low') ?? 0, icon: <ShieldCheck className="h-5 w-5" />, color: 'bg-emerald-100 text-emerald-600' },
      {
        key: 'risk_mapped',
        title: 'Risks Mapped',
        value: riskSummaryData ? `${riskSummaryData.mapped_count}/${riskSummaryData.total}` : '—',
        color: 'bg-blue-100 text-blue-600',
        subtitle: `${riskSummaryData?.unmapped_count ?? 0} unmapped`,
      },
      { key: 'systems', title: 'Systems In Scope', value: systemsData?.total ?? 0, icon: <Server className="h-5 w-5" />, color: 'bg-cyan-100 text-cyan-600' },
      {
        key: 'testing_overdue',
        title: 'Testing Overdue',
        value: overdueData?.testing_overdue ?? 0,
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'see Overdue tab',
      },
      {
        // Urgency-color fix (feedback round 2026-08-23): overdue is always
        // red, never amber — matches "Testing Overdue" above and the
        // days-overdue badges in the Overdue tab.
        key: 'pbc_overdue',
        title: 'PBC Overdue',
        value: overdueData?.pbc_overdue ?? 0,
        icon: <ClipboardX className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'see Overdue tab',
      },
    ];
  }, [riskSummaryData, systemsData, overdueData]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<LayoutDashboard className="h-5 w-5" />}
          iconClassName="bg-purple-50 text-purple-600"
          title="CAE / Manager Dashboard"
          description="Control universe, testing progress, issues, and coverage across the organization"
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />
        <KpiRow items={scopeKpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <ExceptionsSummaryChart controlIdFilter={controlIdFilter} />
                  <IssuesPipelineKanban deptFilter={deptFilter} />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ControlsByDeptChart deptFilter={deptFilter} />
                    <ITGCCoverageBars />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ScopeControlsByRiskChart />
                    <ScopeRisksMappedDonut />
                  </div>
                  <ScopePlannedCompletedChart />
                </div>
              ),
            },
            {
              key: 'analysis',
              label: 'Analysis',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <CoverageMatrixTable />
                    <MRCPanel controlIdFilter={controlIdFilter} />
                  </div>
                  <AuditorRelianceTable controlIdFilter={controlIdFilter} />
                  <TesterWorkloadPanel />
                </div>
              ),
            },
            {
              key: 'overdue',
              label: 'Overdue',
              content: <ScopeOverdueDetail />,
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <div className="grid grid-cols-1 gap-6">
                  <section>
                    <SectionHeader title="Control Universe Inventory" />
                    <ControlUniverseTable rows={filteredUniverseRows} />
                  </section>
                  <section>
                    <SectionHeader title="Testing Status by Control" />
                    <TestingStatusTable controlIdFilter={controlIdFilter} />
                  </section>
                  <section>
                    <SectionHeader title="Action Plan Aging" />
                    <ActionPlanAgingTable />
                  </section>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CAEDashboard;
